import { addCalendarEvent, adminQueryCalendar, createCalendar } from "@/util/functions";
import { DataInstance } from "@busy-human/gearbox";
import { Events, ManagementCalendars } from "@busy-human/k9-library";
// import { getDoc } from "firebase/firestore";
import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { useAuth } from "./auth";

/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-non-null-assertion */

const MONTHS_IN_YEAR = 12;

export const useCalendars = defineStore('calendars', () => {
    const auth = useAuth();

    const calendars = ref(new Map<string, DataInstance<ManagementCalendars.Model>>());
    /** Lazy fetched, events['yyyy-mm'][userId] */
    const events = ref(new Map<`${number}-${number}`, Record<string, DataInstance<Events.Model>[]>>());

    const calendarsLoading = ref(false);
    const eventsLoading = ref(false);
    const loading = computed(() => calendarsLoading.value || eventsLoading.value);

    const main = computed(() => calendars.value.get('Main'));
    const userOwnsMain = computed(() => main.value?.main?.calendarOwner === auth.currentUid);

    const blockedDays = computed(() => calendars.value.get('Blocked'));
    const userOwnsBlocked = computed(() => blockedDays.value?.main?.calendarOwner === auth.currentUid);

    const allowedTimes = computed(() => calendars.value.get('Times'));
    const userOwnsTimes = computed(() => allowedTimes.value?.main?.calendarOwner === auth.currentUid);

    const availableCalendars = computed(() => [...calendars.value.values()].filter(cal => !cal.main));

    async function refreshCalendars() {
        calendarsLoading.value = true;
        const res = await ManagementCalendars.Collection.fetchAll();
        calendars.value.clear();
        for(const doc of res.dataItems()) {
            calendars.value.set(doc.$id, doc);
        }
        console.debug("%c ALLOWED TIMES CALENDAR: ", "background: black; border: 1px solid lime; color: lime;", allowedTimes.value);
        calendarsLoading.value = false;
    }

    function reorganizeCalendarToTrainerEvents(calEvents: Record<string, DataInstance<Events.Model>[]>) {
        const newRecord: Record<string, DataInstance<Events.Model>[]> = {};

        function addToNew(key: string, val: DataInstance<Events.Model>) {
            if(key in newRecord) {
                newRecord[key].push(val);
            } else {
                newRecord[key] = [val];
            }
        }

        for(const [cal, evts] of Object.entries(calEvents)) {
            for(const evt of evts) {
                if('trainerId' in evt && evt.trainerId) addToNew(evt.trainerId, evt);
                else addToNew("Unassigned", evt);
            }
        }

        return newRecord;
    }

    async function fetchEvents(year: number, month: number, force = false) {
        console.debug("%c ALLOWED TIMES CALENDAR: ", "background: black; border: 1px solid lime; color: lime;", allowedTimes.value);
        if(events.value.has(`${year}-${month}`) && !force) return;

        const startTime = new Date(year, month, 1).toISOString();
        const endTime = new Date(year, month + 1, 1).toISOString();

        eventsLoading.value = true;
        const {data: res} = await adminQueryCalendar({startTime, endTime});
        events.value.set(`${year}-${month}`, reorganizeCalendarToTrainerEvents(res));
        eventsLoading.value = false;
    }

    async function addEvent(event: Events.Model, calendarId: string) {
        const {data: eventId} = await addCalendarEvent({calendarId, event});
        const instance: DataInstance<Events.Model> = {
            ... event,
            $id: eventId
        };

        const start = new Date(event.startTime);
        const end = new Date(event.endTime);

        const startMonth = start.getMonth();
        const endMonth = end.getMonth();
        const startYear = start.getFullYear();
        const endYear = end.getFullYear();

        const yearDif = endYear - startYear;
        const monthOffset = endMonth + (MONTHS_IN_YEAR * yearDif) - startMonth;

        for(let i = 0; i <= monthOffset; i++) {
            const newDate = new Date(startYear, startMonth + i, start.getDate());
            const year = newDate.getFullYear();
            const month = newDate.getMonth();
            if(events.value.has(`${year}-${month}`)) {
                const tmp = events.value.get(`${year}-${month}`)!;
                const tmp2 = tmp[calendarId] || [];
                tmp2.push(instance);
                tmp[calendarId] = tmp2;
                events.value.set(`${year}-${month}`, tmp);
            }
        }
        return instance;
    }

    async function addCalendar(name: string) {
        await createCalendar({name});
    }

    // -- Main calendar functions
    /** Sets the current main calendar (for pickup/dropoff times) to the given calendar */
    async function setMainCalendar(cal: ManagementCalendars.Model) {
        if(main.value) throw new Error('Main calendar already exists, have the current owner unset it first.');
        if(!auth.currentUid) throw new Error("What?");

        const tmp: ManagementCalendars.Model = {
            ...cal,
            main: {
                calendarOwner: auth.currentUid,
                isMain: true
            }
        };

        await ManagementCalendars.Collection.docEntity("Main").set(tmp);
    }

    /** Clears the main calendar selection */
    async function clearMain() {
        if(!main.value) throw new Error("Main doesn't exist");
        if(!userOwnsMain.value) throw new Error("User doesn't own main");

        await ManagementCalendars.Collection.deleteDoc("Main");
    }

    // -- Blocked days calendar functions
    async function setBlocked(cal: ManagementCalendars.Model) {
        if(blockedDays.value) throw new Error('Blocked days calendar already exists, have the current owner unset it first.');
        if(!auth.currentUid) throw new Error("What?");

        const tmp: ManagementCalendars.Model = {
            ...cal,
            main: {
                calendarOwner: auth.currentUid,
                isMain: true
            },
            hidden: true
        };

        await ManagementCalendars.Collection.docEntity("Blocked").set(tmp);
    }

    async function clearBlocked() {
        if(!blockedDays.value) throw new Error("Blocked days calendar doesn't exist");
        if(!userOwnsBlocked.value) throw new Error("User doesn't own blocked days calendar");

        await ManagementCalendars.Collection.deleteDoc("Blocked");
    }

    // -- Acceptable times calendar functions
    async function setAllowedTimes(cal: ManagementCalendars.Model) {
        if(allowedTimes.value) throw new Error('Allowed times calendar already exists, have the current owner unset it first.');
        if(!auth.currentUid) throw new Error("What?");

        const tmp: ManagementCalendars.Model = {
            ...cal,
            main: {
                calendarOwner: auth.currentUid,
                isMain: true
            },
            hidden: true
        };

        await ManagementCalendars.Collection.docEntity("Times").set(tmp);
    }

    async function clearAllowedTimes() {
        if(!allowedTimes.value) throw new Error("Allowed times calendar doesn't exist");
        if(!userOwnsTimes.value) throw new Error("User doesn't own allowed times calendar");

        await ManagementCalendars.Collection.deleteDoc("Times");
    }

    // Misc

    function clearEvents() {
        events.value.clear();
    }

    watch(() => auth.currentUid, async () => {
        await refreshCalendars();
    }, { immediate: true });


    // Helpers so the return isn't so long
    const _mainfuncs = {
        main, setMainCalendar, userOwnsMain, clearMain
    };
    const _blockedfuncs = {
        blockedDays, setBlocked, userOwnsBlocked, clearBlocked
    };
    const _allowedfuncs = {
        allowedTimes, setAllowedTimes, userOwnsTimes, clearAllowedTimes
    };

    return { loading, refreshCalendars, calendars, availableCalendars, fetchEvents, events, clearEvents, addEvent, addCalendar,
        ..._mainfuncs,
        ..._blockedfuncs,
        ..._allowedfuncs };
});